import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './TtsSurvey.css';

const COST_PER_CALL = 30;

interface Choice {
  key: string;
  label: string;
}

interface Question {
  id?: number;
  qOrder: number;
  type: string;
  ttsBody: string;
  choices?: Choice[];
}

interface Survey {
  id?: number;
  title: string;
  questions: Question[];
}

interface SurveyResult {
  id: string;
  phoneNumber: string;
  status: 'pending' | 'calling' | 'success' | 'failed';
  surveyTitle: string;
  timestamp: string;
}

interface SurveyHistory {
  id: string;
  phoneNumbers: string;
  timestamp: string;
  count: number;
  surveyTitle: string;
}

interface SurveyAnswerDto {
  questionId: number;
  questionText: string;
  answer: string;
  recordedAt: string;
}

interface FullSurveyHistory {
  id: number;
  companyId: number;
  phoneNumber: string;
  message: string;
  status: 'success' | 'failed' | 'pending';
  serviceType: string;
  sentAt: string;
  cost: number;
  errorMessage?: string;
  surveyTitle?: string;
  surveyAnswers?: SurveyAnswerDto[];
  eventCount: number;
}

const QUESTION_TYPES = [
  { value: 'single', label: '단일 선택' },
  { value: 'multi', label: '다중 선택' },
  { value: 'yesno', label: '예/아니오' },
  { value: 'score', label: '점수 (1-5)' },
  { value: 'numeric', label: '숫자 입력' }
];

const TtsSurvey: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'builder' | 'send' | 'list' | 'report'>('builder');
  const [survey, setSurvey] = useState<Survey>({
    title: '',
    questions: []
  });
  const [renderKey, setRenderKey] = useState(0); // 강제 리렌더링용
  const [savedSurveys, setSavedSurveys] = useState<any[]>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState('');
  const [loading, setLoading] = useState(false);
  const [surveyResults, setSurveyResults] = useState<SurveyResult[]>([]);
  const [selectedReportSurveyId, setSelectedReportSurveyId] = useState<number | null>(null);
  const [surveyReport, setSurveyReport] = useState<any>(null);

  // 파일 업로드 및 히스토리 관련 state
  const [surveyHistory, setSurveyHistory] = useState<SurveyHistory[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<string[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [fileRows, setFileRows] = useState<string[][]>([]);
  const [selectedColumn, setSelectedColumn] = useState<number>(-1);
  const [showFullHistoryModal, setShowFullHistoryModal] = useState(false);
  const [fullSurveyHistory, setFullSurveyHistory] = useState<FullSurveyHistory[]>([]);
  const [loadingFullHistory, setLoadingFullHistory] = useState(false);

  // 필터 상태
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterServiceType, setFilterServiceType] = useState<string>('TTS_RS'); // 설문만 기본값
  const [filterPhoneNumber, setFilterPhoneNumber] = useState<string>('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // API Base URL 설정
  const hostname = window.location.hostname;
  const API_BASE_URL = process.env.REACT_APP_API_URL ||
    (hostname === 'localhost' ? 'http://localhost:5111' : `http://${hostname}:5111`);

  useEffect(() => {
    if (activeTab === 'list' || activeTab === 'send') {
      loadSurveys();
    }
    if (activeTab === 'send') {
      loadSurveyHistory();
    }
  }, [activeTab]);

  const loadSurveys = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/survey`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSavedSurveys(data.data);
      }
    } catch (error) {
      console.error('설문 목록 로드 실패:', error);
    }
  };

  const addQuestion = () => {
    // 설문은 1개의 질문만 허용
    if (survey.questions.length >= 1) {
      alert('설문은 1개의 질문만 등록할 수 있습니다.');
      return;
    }
    const newQuestion: Question = {
      qOrder: 1,
      type: 'single',
      ttsBody: '',
      choices: []
    };
    setSurvey({ ...survey, questions: [newQuestion] });
  };

  const removeQuestion = (index: number) => {
    if (!window.confirm('질문을 삭제하시겠습니까?')) {
      return;
    }

    const updatedQuestions = survey.questions.filter((_, i) => i !== index);
    const reorderedQuestions = updatedQuestions.map((q, i) => ({
      ...q,
      qOrder: i + 1
    }));

    setSurvey({
      id: survey.id,
      title: survey.title,
      questions: reorderedQuestions
    });

    setRenderKey(prev => prev + 1);
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updatedQuestions = [...survey.questions];
    if (field === 'type') {
      updatedQuestions[index].type = value;
      // 타입 변경 시 기본 선택지 설정
      if (value === 'yesno') {
        updatedQuestions[index].choices = [
          { key: '1', label: '예' },
          { key: '2', label: '아니오' }
        ];
      } else if (value === 'score') {
        updatedQuestions[index].choices = [
          { key: '1', label: '매우 나쁨' },
          { key: '2', label: '나쁨' },
          { key: '3', label: '보통' },
          { key: '4', label: '좋음' },
          { key: '5', label: '매우 좋음' }
        ];
      } else if (value === 'numeric') {
        updatedQuestions[index].choices = [];
      }
    } else {
      (updatedQuestions[index] as any)[field] = value;
    }
    setSurvey({ ...survey, questions: updatedQuestions });
  };

  const addChoice = (questionIndex: number) => {
    const updatedQuestions = [...survey.questions];
    if (!updatedQuestions[questionIndex].choices) {
      updatedQuestions[questionIndex].choices = [];
    }
    const choiceNumber = (updatedQuestions[questionIndex].choices?.length || 0) + 1;
    updatedQuestions[questionIndex].choices!.push({
      key: choiceNumber.toString(),
      label: ''
    });
    setSurvey({ ...survey, questions: updatedQuestions });
  };

  const updateChoice = (questionIndex: number, choiceIndex: number, field: string, value: string) => {
    const updatedQuestions = [...survey.questions];
    if (updatedQuestions[questionIndex].choices) {
      (updatedQuestions[questionIndex].choices![choiceIndex] as any)[field] = value;
      setSurvey({ ...survey, questions: updatedQuestions });
    }
  };

  const removeChoice = (questionIndex: number, choiceIndex: number) => {
    const updatedQuestions = [...survey.questions];
    updatedQuestions[questionIndex].choices = updatedQuestions[questionIndex].choices?.filter((_, i) => i !== choiceIndex);
    setSurvey({ ...survey, questions: updatedQuestions });
  };

  const saveSurvey = async () => {
    if (!survey.title || survey.questions.length === 0) {
      alert('설문 제목과 질문을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const url = survey.id ? `${API_BASE_URL}/api/survey/${survey.id}` : `${API_BASE_URL}/api/survey`;
      const method = survey.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          title: survey.title,
          questions: survey.questions
        })
      });

      const data = await response.json();
      if (data.success) {
        alert('설문이 저장되었습니다.');
        setSurvey({
          title: '',
          questions: []
        });
        setActiveTab('list');
      } else {
        alert('설문 저장 실패: ' + data.message);
      }
    } catch (error) {
      console.error('설문 저장 실패:', error);
      alert('설문 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadSurveyForEdit = async (surveyId: number) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/survey/${surveyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setSurvey({
          id: data.data.id,
          title: data.data.title,
          questions: data.data.questions
        });
        setActiveTab('builder');
      }
    } catch (error) {
      console.error('설문 로드 실패:', error);
    }
  };

  const deleteSurvey = async (surveyId: number) => {
    if (!window.confirm('정말 이 설문을 삭제하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/survey/${surveyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        alert('설문이 삭제되었습니다.');
        await loadSurveys();
      } else {
        alert(`삭제 실패: ${data.message || '알 수 없는 오류'}`);
      }
    } catch (error) {
      console.error('설문 삭제 실패:', error);
      alert('설문 삭제 중 오류가 발생했습니다.');
    }
  };

  const loadSurveyHistory = () => {
    const savedHistory = localStorage.getItem('surveyHistory');
    if (savedHistory) {
      setSurveyHistory(JSON.parse(savedHistory));
    }
  };

  const saveToHistory = (phoneNumbers: string, surveyTitle: string) => {
    const newHistory: SurveyHistory = {
      id: Date.now().toString(),
      phoneNumbers,
      timestamp: new Date().toISOString(),
      count: phoneNumbers.split('|').length,
      surveyTitle
    };
    const updated = [newHistory, ...surveyHistory].slice(0, 20);
    setSurveyHistory(updated);
    localStorage.setItem('surveyHistory', JSON.stringify(updated));
  };

  // 구분자 자동 감지
  const detectDelimiter = (line: string): string => {
    const delimiters = [',', '\t', ';', '|'];
    let maxCount = 0;
    let bestDelimiter = ',';

    for (const delimiter of delimiters) {
      const count = line.split(delimiter).length;
      if (count > maxCount) {
        maxCount = count;
        bestDelimiter = delimiter;
      }
    }

    return maxCount > 1 ? bestDelimiter : ',';
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');

    if (isExcel) {
      // 엑셀 파일 처리
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const data = event.target?.result;
          const workbook = XLSX.read(data, { type: 'array' });

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

          if (jsonData.length === 0) {
            alert('파일이 비어있습니다.');
            return;
          }

          const headers = jsonData[0].map((h: any) => String(h || '').trim());
          const rows = jsonData.slice(1).map(row =>
            row.map((cell: any) => String(cell || '').trim())
          );

          setFileHeaders(headers);
          setFileRows(rows);
          setSelectedColumn(-1);
          setShowColumnModal(true);
        } catch (error) {
          console.error('Excel parsing error:', error);
          alert('엑셀 파일을 읽는 중 오류가 발생했습니다.');
        }
      };

      reader.readAsArrayBuffer(file);
    } else {
      // CSV/TXT 파일 처리
      const reader = new FileReader();
      reader.onload = (event) => {
        const content = event.target?.result as string;
        const lines = content.split(/[\r\n]+/).filter(line => line.trim());

        if (lines.length === 0) {
          alert('파일이 비어있습니다.');
          return;
        }

        const delimiter = detectDelimiter(lines[0]);
        const headers = lines[0].split(delimiter).map(h => h.trim());
        const rows = lines.slice(1).map(line =>
          line.split(delimiter).map(cell => cell.trim())
        );

        setFileHeaders(headers);
        setFileRows(rows);
        setSelectedColumn(-1);
        setShowColumnModal(true);
      };

      reader.readAsText(file);
    }

    e.target.value = '';
  };

  const handleColumnSelect = () => {
    if (selectedColumn === -1) {
      alert('컬럼을 선택해주세요.');
      return;
    }

    const phoneNumbersFromFile = fileRows
      .map(row => row[selectedColumn])
      .filter(phone => phone && phone.trim())
      .map(phone => phone.trim());

    if (phoneNumbersFromFile.length === 0) {
      alert('선택한 컬럼에 유효한 데이터가 없습니다.');
      return;
    }

    const existing = phoneNumbers ? phoneNumbers.split('|') : [];
    const combined = [...existing, ...phoneNumbersFromFile].filter(num => num);

    setPhoneNumbers(combined.join('|'));

    setShowColumnModal(false);
    setFileHeaders([]);
    setFileRows([]);
    setSelectedColumn(-1);

    alert(`${phoneNumbersFromFile.length}개의 전화번호가 추가되었습니다.`);
  };

  const handleHistorySelect = (history: SurveyHistory) => {
    const phones = history.phoneNumbers.split('|');
    const currentSelected = new Set(selectedHistory);

    phones.forEach(phone => {
      if (currentSelected.has(phone)) {
        currentSelected.delete(phone);
      } else {
        currentSelected.add(phone);
      }
    });

    setSelectedHistory(Array.from(currentSelected));
  };

  const addSelectedToForm = () => {
    const existing = phoneNumbers ? phoneNumbers.split('|') : [];
    const allPhones = [...existing, ...selectedHistory];
    const combined = Array.from(new Set(allPhones));
    setPhoneNumbers(combined.join('|'));
    setSelectedHistory([]);
    setShowHistoryModal(false);
  };

  const sendSurvey = async () => {
    if (!selectedSurveyId) {
      alert('발송할 설문을 선택해주세요.');
      return;
    }

    if (!phoneNumbers) {
      alert('전화번호를 입력해주세요.');
      return;
    }

    const phoneCount = getPhoneCount();
    const totalCost = phoneCount * COST_PER_CALL;

    if ((user?.creditBalance || 0) < totalCost) {
      alert('크레딧이 부족합니다.');
      return;
    }

    // 전화번호 추출 및 결과 초기화
    const phones = phoneNumbers.split('|').map(p => p.trim().replace(/-/g, '')).filter(p => p);
    const selectedSurvey = savedSurveys.find(s => s.id === selectedSurveyId);
    const surveyTitle = selectedSurvey?.title || '설문';

    // 발송 내역 저장
    saveToHistory(phoneNumbers, surveyTitle);

    const results: SurveyResult[] = phones.map(phone => ({
      id: `${Date.now()}-${phone}`,
      phoneNumber: phone,
      status: 'pending',
      surveyTitle: surveyTitle,
      timestamp: new Date().toISOString()
    }));
    setSurveyResults(results);

    setLoading(true);
    try {
      setSurveyResults(prev => prev.map(r => ({ ...r, status: 'calling' })));

      const response = await fetch(`${API_BASE_URL}/api/survey/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          surveyId: selectedSurveyId,
          phoneNumbers: phoneNumbers
        })
      });

      const data = await response.json();
      if (data.success) {
        setSurveyResults(prev => prev.map(r => ({ ...r, status: 'success' })));
        alert(`설문이 발송되었습니다.\n발송 건수: ${data.data.sentCount}건\n총 비용: ${data.data.totalCost}원`);
        setPhoneNumbers('');
        setSelectedSurveyId(null);
      } else {
        setSurveyResults(prev => prev.map(r => ({ ...r, status: 'failed' })));
        alert('설문 발송 실패: ' + data.message);
      }
    } catch (error) {
      console.error('설문 발송 실패:', error);
      setSurveyResults(prev => prev.map(r => ({ ...r, status: 'failed' })));
      alert('설문 발송 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadSurveyReport = async (surveyId: number) => {
    if (!surveyId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/survey/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          surveyId: surveyId
        })
      });

      const data = await response.json();
      if (data.success) {
        setSurveyReport(data.data);
      } else {
        alert('리포트 조회 실패: ' + data.message);
      }
    } catch (error) {
      console.error('리포트 조회 실패:', error);
      alert('리포트 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getPhoneCount = () => {
    if (!phoneNumbers) return 0;
    return phoneNumbers.split('|').filter(p => p.trim()).length;
  };

  const getTotalCost = () => {
    return getPhoneCount() * COST_PER_CALL;
  };

  const loadFullSurveyHistory = async () => {
    setLoadingFullHistory(true);
    try {
      // 필터 파라미터 구성
      const params = new URLSearchParams();
      if (filterStartDate) params.append('startDate', filterStartDate);
      if (filterEndDate) params.append('endDate', filterEndDate);
      if (filterStatus) params.append('status', filterStatus);
      if (filterPhoneNumber) params.append('phoneNumber', filterPhoneNumber);
      // TTS 설문조사 페이지에서는 TTS_RS이면서 배차가 아닌 것만 표시
      params.append('serviceType', 'TTS_RS');
      params.append('isDispatch', 'false');

      const response = await fetch(`${API_BASE_URL}/api/call-history?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('발신 내역을 불러올 수 없습니다.');
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // 시간 역정렬 (최신순)
        const sortedData = data.sort((a: FullSurveyHistory, b: FullSurveyHistory) =>
          new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
        );
        setFullSurveyHistory(sortedData);
      }
    } catch (error: any) {
      console.error('Survey history load error:', error);
      alert(`발신 내역 조회 오류: ${error.message || '알 수 없는 오류'}`);
    } finally {
      setLoadingFullHistory(false);
    }
  };

  const downloadCSV = () => {
    // 필터 파라미터 구성
    const params = new URLSearchParams();
    if (filterStartDate) params.append('startDate', filterStartDate);
    if (filterEndDate) params.append('endDate', filterEndDate);
    if (filterStatus) params.append('status', filterStatus);
    if (filterPhoneNumber) params.append('phoneNumber', filterPhoneNumber);
    // TTS 설문조사 페이지에서는 TTS_RS이면서 배차가 아닌 것만 다운로드
    params.append('serviceType', 'TTS_RS');
    params.append('isDispatch', 'false');

    const url = `${API_BASE_URL}/api/call-history/export-csv?${params.toString()}`;
    const token = localStorage.getItem('token');

    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `survey_history_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      })
      .catch(error => {
        console.error('CSV download error:', error);
        alert('CSV 다운로드 중 오류가 발생했습니다.');
      });
  };

  const applyFilters = () => {
    loadFullSurveyHistory();
  };

  const resetFilters = () => {
    setFilterStatus('');
    setFilterServiceType('TTS_RS');
    setFilterPhoneNumber('');
    setFilterStartDate('');
    setFilterEndDate('');
  };

  const openFullHistoryModal = () => {
    setShowFullHistoryModal(true);
    loadFullSurveyHistory();
  };

  return (
    <div className="tts-survey-page">
      <section className="tts-survey-content section">
        <div className="container-narrow">
          <div className="tts-survey-card card">
            <h2 className="card-title">TTS 설문조사 관리</h2>

            {/* 탭 메뉴 */}
            <div className="survey-tabs">
              <button
                className={`tab-button ${activeTab === 'builder' ? 'active' : ''}`}
                onClick={() => setActiveTab('builder')}
              >
                설문 빌더
              </button>
              <button
                className={`tab-button ${activeTab === 'send' ? 'active' : ''}`}
                onClick={() => setActiveTab('send')}
              >
                설문 발송
              </button>
              <button
                className={`tab-button ${activeTab === 'list' ? 'active' : ''}`}
                onClick={() => setActiveTab('list')}
              >
                설문 목록
              </button>
              <button
                className={`tab-button ${activeTab === 'report' ? 'active' : ''}`}
                onClick={() => { setActiveTab('report'); loadSurveys(); }}
              >
                결과 리포트
              </button>
            </div>

            {/* 설문 빌더 탭 */}
            {activeTab === 'builder' && (
              <div className="survey-builder">
                <div className="form-group">
                  <label className="form-label">설문 제목 *</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="예: 고객 만족도 조사"
                    value={survey.title}
                    onChange={(e) => setSurvey({ ...survey, title: e.target.value })}
                  />
                </div>

                <div className="questions-section" key={`questions-section-${renderKey}`}>
                  <div className="section-header">
                    <h3>질문 목록</h3>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={addQuestion}
                      disabled={survey.questions.length >= 1}
                    >
                      질문 추가
                    </button>
                  </div>

                  {survey.questions.map((question, qIndex) => (
                    <div key={`question-${qIndex}-${question.qOrder}-${question.type}`} className="question-card">
                      <div className="question-header">
                        <h4>질문 {question.qOrder}</h4>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeQuestion(qIndex);
                          }}
                          style={{ fontSize: '14px', padding: '8px 16px', fontWeight: 'bold' }}
                        >
                          🗑️ 질문 삭제
                        </button>
                      </div>

                      <div className="form-group">
                        <label className="form-label">질문 유형</label>
                        <select
                          className="form-select"
                          value={question.type}
                          onChange={(e) => updateQuestion(qIndex, 'type', e.target.value)}
                        >
                          {QUESTION_TYPES.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">질문 내용 (TTS로 읽힐 텍스트)</label>
                        <textarea
                          className="form-textarea"
                          placeholder="예: 우리 서비스에 만족하십니까?"
                          value={question.ttsBody}
                          onChange={(e) => updateQuestion(qIndex, 'ttsBody', e.target.value)}
                          rows={3}
                        />
                      </div>

                      {/* 선택지 (single, multi 타입) */}
                      {(question.type === 'single' || question.type === 'multi') && (
                        <div className="choices-section">
                          <div className="choices-header">
                            <label className="form-label">선택지</label>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => addChoice(qIndex)}
                            >
                              선택지 추가
                            </button>
                          </div>
                          {question.choices?.map((choice, cIndex) => (
                            <div key={cIndex} className="choice-row">
                              <input
                                type="text"
                                className="choice-key"
                                placeholder="키"
                                value={choice.key}
                                onChange={(e) => updateChoice(qIndex, cIndex, 'key', e.target.value)}
                              />
                              <input
                                type="text"
                                className="choice-label"
                                placeholder="선택지 레이블"
                                value={choice.label}
                                onChange={(e) => updateChoice(qIndex, cIndex, 'label', e.target.value)}
                              />
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => removeChoice(qIndex, cIndex)}
                              >
                                삭제
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* 예/아니오 선택지 표시 */}
                      {question.type === 'yesno' && (
                        <div className="info-box">
                          선택지: 1번 예, 2번 아니오
                        </div>
                      )}

                      {/* 점수 선택지 표시 */}
                      {question.type === 'score' && (
                        <div className="info-box">
                          선택지: 1번 매우 나쁨, 2번 나쁨, 3번 보통, 4번 좋음, 5번 매우 좋음
                        </div>
                      )}

                      {/* 숫자 입력 안내 */}
                      {question.type === 'numeric' && (
                        <div className="info-box">
                          사용자가 DTMF로 숫자를 입력합니다.
                        </div>
                      )}
                    </div>
                  ))}

                  {survey.questions.length === 0 && (
                    <div className="empty-state">
                      질문을 추가해주세요.
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    onClick={saveSurvey}
                    disabled={loading || !survey.title || survey.questions.length === 0}
                  >
                    {loading ? '저장 중...' : '설문 저장'}
                  </button>
                </div>
              </div>
            )}

            {/* 설문 발송 탭 */}
            {activeTab === 'send' && (
              <div className="survey-send">
                {/* 메인 폼 */}
                <div className="survey-send-main">
                  <div className="card">
                    <h3 className="card-title">발신 정보</h3>

                    <div className="form-group">
                      <label className="form-label">발송할 설문 선택 *</label>
                      <select
                        className="form-select"
                        value={selectedSurveyId || ''}
                        onChange={(e) => setSelectedSurveyId(Number(e.target.value))}
                      >
                        <option value="">설문을 선택하세요</option>
                        {savedSurveys
                          .filter(s => s.isActive && !s.title.includes('배차 확인 - 템플릿'))
                          .map(s => (
                            <option key={s.id} value={s.id}>
                              {s.title} ({s.questionCount}개 질문)
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        전화번호 *
                        <span className="form-hint">
                          (구분자: | 예: 010-1111-1111|010-2222-2222)
                        </span>
                      </label>
                      <div className="phone-input-group">
                        <textarea
                          className="form-textarea phone-textarea"
                          placeholder="010-1234-5678|010-2345-6789|..."
                          value={phoneNumbers}
                          onChange={(e) => setPhoneNumbers(e.target.value)}
                          rows={4}
                        />
                        <div className="phone-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setShowHistoryModal(true)}
                          >
                            이전 내역에서 선택
                          </button>
                          <label className="btn btn-secondary btn-sm file-upload-btn">
                            파일 업로드 (CSV/Excel)
                            <input
                              type="file"
                              accept=".csv,.txt,.xlsx,.xls"
                              onChange={handleFileUpload}
                              style={{ display: 'none' }}
                            />
                          </label>
                        </div>
                        <div className="phone-count">
                          총 {getPhoneCount()}개의 번호
                        </div>
                      </div>
                    </div>

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-primary btn-lg"
                        onClick={sendSurvey}
                        disabled={loading || !selectedSurveyId || getPhoneCount() === 0 || (user?.creditBalance || 0) < getTotalCost()}
                      >
                        {loading ? '발송 중...' : `설문 발송 (${getPhoneCount()}건 / ${getTotalCost().toLocaleString()}원)`}
                      </button>
                    </div>
                  </div>
                </div>

                {/* 사이드바 - 실시간 현황 */}
                <div className="survey-send-sidebar">
                  {/* 전체 발신 내역 버튼 */}
                  <button
                    type="button"
                    onClick={openFullHistoryModal}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      marginBottom: '1rem',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#374151',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f3f4f6';
                      e.currentTarget.style.borderColor = '#9ca3af';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                      e.currentTarget.style.borderColor = '#d1d5db';
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>📋</span>
                    <span>전체 발신 내역</span>
                  </button>

                  <div className="card">
                    <h3 className="card-title">실시간 발송 현황</h3>
                    <div className="survey-results">
                      {surveyResults.length === 0 ? (
                        <div className="empty-state">
                          <p>발송을 시작하면 여기에 결과가 표시됩니다</p>
                        </div>
                      ) : (
                        <>
                          <div className="results-list">
                            {surveyResults.slice(0, 5).map(result => (
                              <div key={result.id} className={`result-item status-${result.status}`}>
                                <div className="result-phone">{result.phoneNumber}</div>
                                <div className="result-status">
                                  {result.status === 'pending' && '대기중'}
                                  {result.status === 'calling' && '발송중...'}
                                  {result.status === 'success' && '✓ 성공'}
                                  {result.status === 'failed' && '✗ 실패'}
                                </div>
                              </div>
                            ))}
                          </div>
                          {surveyResults.length > 5 && (
                            <div style={{ textAlign: 'center', padding: '0.5rem', color: '#6b7280', fontSize: '0.85rem' }}>
                              외 {surveyResults.length - 5}건
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* 크레딧 정보 */}
                  <div className="card" style={{ marginTop: '1.5rem' }}>
                    <h3 className="card-title">크레딧 및 비용 정보</h3>
                    <div className="credit-info-box">
                      <div className="credit-info-item">
                        <span className="info-label">보유 크레딧</span>
                        <span className="info-value">{(user?.creditBalance || 0).toLocaleString()}원</span>
                      </div>
                      <div className="credit-info-item">
                        <span className="info-label">발신 건수</span>
                        <span className="info-value highlight">{getPhoneCount()}건</span>
                      </div>
                      <div className="credit-info-item">
                        <span className="info-label">예상 비용</span>
                        <span className="info-value cost">{getTotalCost().toLocaleString()}원</span>
                      </div>
                      <div className="credit-info-item">
                        <span className="info-label">발신 후 잔액</span>
                        <span className={`info-value ${(user?.creditBalance || 0) - getTotalCost() < 0 ? 'warning' : ''}`}>
                          {Math.max(0, (user?.creditBalance || 0) - getTotalCost()).toLocaleString()}원
                        </span>
                      </div>
                    </div>
                    <p style={{ marginTop: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
                      ※ 크레딧은 발신 성공 시에만 차감되며, 발신 실패 시에는 차감되지 않습니다.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* 설문 목록 탭 */}
            {activeTab === 'list' && (
              <div className="survey-list">
                {savedSurveys.filter(s => !s.title.includes('배차 확인 - 템플릿')).length === 0 ? (
                  <div className="empty-state">
                    저장된 설문이 없습니다.
                  </div>
                ) : (
                  <div className="survey-items">
                    {savedSurveys.filter(s => !s.title.includes('배차 확인 - 템플릿')).map(survey => (
                      <div key={survey.id} className="survey-item">
                        <div className="survey-info">
                          <h3>{survey.title}</h3>
                          <div className="survey-meta">
                            <span>{survey.questionCount}개 질문</span>
                            <span>{survey.sessionCount}개 응답</span>
                            <span>{survey.isActive ? '활성' : '비활성'}</span>
                          </div>
                        </div>
                        <div className="survey-actions">
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => loadSurveyForEdit(survey.id)}
                          >
                            수정
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => deleteSurvey(survey.id)}
                          >
                            삭제
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 결과 리포트 탭 */}
            {activeTab === 'report' && (
              <div className="survey-report">
                <div className="form-group">
                  <label className="form-label">설문 선택 *</label>
                  <select
                    className="form-select"
                    value={selectedReportSurveyId || ''}
                    onChange={(e) => {
                      const surveyId = Number(e.target.value);
                      setSelectedReportSurveyId(surveyId);
                      if (surveyId) {
                        loadSurveyReport(surveyId);
                      }
                    }}
                  >
                    <option value="">설문을 선택하세요</option>
                    {savedSurveys.filter(s => !s.title.includes('배차 확인 - 템플릿')).map(s => (
                      <option key={s.id} value={s.id}>
                        {s.title}
                      </option>
                    ))}
                  </select>
                </div>

                {loading && (
                  <div className="empty-state">
                    <p>리포트를 불러오는 중...</p>
                  </div>
                )}

                {!loading && surveyReport && (
                  <div className="report-content">
                    <div className="report-header">
                      <h3>{surveyReport.surveyTitle}</h3>
                    </div>

                    <div className="report-summary">
                      <div className="summary-card">
                        <div className="summary-label">총 응답 세션</div>
                        <div className="summary-value">{surveyReport.totalSessions}개</div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">완료된 세션</div>
                        <div className="summary-value success">{surveyReport.completedSessions}개</div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">중단된 세션</div>
                        <div className="summary-value warning">{surveyReport.abandonedSessions}개</div>
                      </div>
                      <div className="summary-card">
                        <div className="summary-label">완료율</div>
                        <div className="summary-value highlight">{surveyReport.completionRate.toFixed(1)}%</div>
                      </div>
                    </div>

                    {surveyReport.questions && surveyReport.questions.map((question: any) => (
                      <div key={question.questionId} className="question-report">
                        <div className="question-report-header">
                          <h4>질문 {question.qOrder}</h4>
                          <span className="question-type">{question.type}</span>
                        </div>
                        <p className="question-body">{question.ttsBody}</p>
                        <div className="answer-stats">
                          <div className="stats-header">
                            <span>총 응답: {question.totalAnswers}개</span>
                            {question.averageLatencyMs && (
                              <span>평균 응답 시간: {question.averageLatencyMs.toFixed(0)}ms</span>
                            )}
                          </div>
                          <div className="answer-distribution">
                            {Object.entries(question.answerDistribution).map(([key, count]: [string, any]) => {
                              const percentage = question.totalAnswers > 0
                                ? ((count / question.totalAnswers) * 100).toFixed(1)
                                : '0';
                              return (
                                <div key={key} className="distribution-item">
                                  <div className="distribution-label">
                                    <span className="answer-key">{key}번</span>
                                    <span className="answer-count">{count}명 ({percentage}%)</span>
                                  </div>
                                  <div className="distribution-bar">
                                    <div
                                      className="distribution-fill"
                                      style={{ width: `${percentage}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {!loading && !surveyReport && selectedReportSurveyId && (
                  <div className="empty-state">
                    <p>응답 데이터가 없습니다.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 컬럼 선택 모달 */}
          {showColumnModal && (
            <div className="modal-overlay" onClick={() => setShowColumnModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>전화번호 컬럼 선택</h2>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={() => setShowColumnModal(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                    파일에서 전화번호가 포함된 컬럼을 선택해주세요.
                  </p>
                  <div className="column-selector">
                    {fileHeaders.map((header, index) => (
                      <div key={index} className="column-option">
                        <label className="column-label">
                          <input
                            type="radio"
                            name="column"
                            value={index}
                            checked={selectedColumn === index}
                            onChange={() => setSelectedColumn(index)}
                          />
                          <div className="column-info">
                            <div className="column-name">{header || `컬럼 ${index + 1}`}</div>
                            <div className="column-preview">
                              미리보기: {fileRows.slice(0, 3).map(row => row[index]).join(', ')}
                              {fileRows.length > 3 && '...'}
                            </div>
                            <div className="column-count">
                              {fileRows.filter(row => row[index] && row[index].trim()).length}개 항목
                            </div>
                          </div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowColumnModal(false)}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleColumnSelect}
                    disabled={selectedColumn === -1}
                  >
                    선택 완료
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 이전 내역 모달 */}
          {showHistoryModal && (
            <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>이전 발신 내역</h2>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={() => setShowHistoryModal(false)}
                  >
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  {surveyHistory.length === 0 ? (
                    <div className="empty-state">
                      <p>저장된 발신 내역이 없습니다</p>
                    </div>
                  ) : (
                    <div className="history-list">
                      {surveyHistory.map(history => (
                        <div key={history.id} className="history-item">
                          <div className="history-info">
                            <div className="history-title">{history.surveyTitle}</div>
                            <div className="history-date">
                              {new Date(history.timestamp).toLocaleString('ko-KR')}
                            </div>
                            <div className="history-count">
                              {history.count}개 번호
                            </div>
                          </div>
                          <div className="history-phones">
                            {history.phoneNumbers.split('|').slice(0, 3).join(', ')}
                            {history.count > 3 && ` 외 ${history.count - 3}개`}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleHistorySelect(history)}
                            >
                              {history.phoneNumbers.split('|').every(p => selectedHistory.includes(p))
                                ? '선택 해제'
                                : '선택'}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={() => {
                                const updated = surveyHistory.filter(h => h.id !== history.id);
                                setSurveyHistory(updated);
                                localStorage.setItem('surveyHistory', JSON.stringify(updated));
                              }}
                              style={{
                                padding: '4px 12px',
                                fontSize: '0.875rem',
                                color: '#ef4444',
                                backgroundColor: 'transparent',
                                border: '1px solid #ef4444',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              삭제
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <div className="selected-count">
                    선택된 번호: {selectedHistory.length}개
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={addSelectedToForm}
                    disabled={selectedHistory.length === 0}
                  >
                    선택한 번호 추가
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 전체 발신 내역 모달 */}
          {showFullHistoryModal && (
            <div className="modal-overlay" onClick={() => setShowFullHistoryModal(false)}>
              <div className="modal-content" style={{ maxWidth: '1100px', width: '95%' }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>전체 설문 발신 내역 (응답 결과 기준)</h2>
                  <button
                    className="modal-close"
                    onClick={() => setShowFullHistoryModal(false)}
                  >
                    ✕
                  </button>
                </div>

                {/* 필터 섹션 */}
                <div style={{ padding: '16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>시작일</label>
                      <input
                        type="date"
                        value={filterStartDate}
                        onChange={(e) => setFilterStartDate(e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>종료일</label>
                      <input
                        type="date"
                        value={filterEndDate}
                        onChange={(e) => setFilterEndDate(e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>전화번호</label>
                      <input
                        type="text"
                        value={filterPhoneNumber}
                        onChange={(e) => setFilterPhoneNumber(e.target.value)}
                        placeholder="전화번호 검색"
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>상태</label>
                      <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      >
                        <option value="">전체</option>
                        <option value="success">성공</option>
                        <option value="failed">실패</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '4px', display: 'block' }}>유형</label>
                      <select
                        value={filterServiceType}
                        onChange={(e) => setFilterServiceType(e.target.value)}
                        style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                      >
                        <option value="">전체</option>
                        <option value="TTS_MSG">일반</option>
                        <option value="TTS_RS">설문</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                      <button
                        onClick={applyFilters}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        필터 적용
                      </button>
                      <button
                        onClick={resetFilters}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        초기화
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      onClick={downloadCSV}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>📥</span>
                      <span>CSV 다운로드</span>
                    </button>
                  </div>
                </div>

                <div className="modal-body">
                  {loadingFullHistory ? (
                    <div className="empty-state">
                      <p>발신 내역을 불러오는 중...</p>
                    </div>
                  ) : fullSurveyHistory.length === 0 ? (
                    <div className="empty-state">
                      <p>발신 내역이 없습니다</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                            <th style={{ padding: '12px 8px', textAlign: 'left' }}>발신일시</th>
                            <th style={{ padding: '12px 8px', textAlign: 'center' }}>유형</th>
                            <th style={{ padding: '12px 8px', textAlign: 'left' }}>전화번호</th>
                            <th style={{ padding: '12px 8px', textAlign: 'left' }}>메시지</th>
                            <th style={{ padding: '12px 8px', textAlign: 'center' }}>설문응답</th>
                            <th style={{ padding: '12px 8px', textAlign: 'center' }}>상태</th>
                            <th style={{ padding: '12px 8px', textAlign: 'right' }}>비용</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fullSurveyHistory.map((item) => (
                            <tr key={item.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                              <td style={{ padding: '12px 8px' }}>
                                {new Date(item.sentAt).toLocaleString('ko-KR', {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                  second: '2-digit'
                                })}
                              </td>
                              <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                <span style={{
                                  padding: '3px 8px',
                                  borderRadius: '8px',
                                  fontSize: '0.8rem',
                                  fontWeight: '500',
                                  backgroundColor: item.serviceType === 'TTS_RS' ? '#dbeafe' : '#e0e7ff',
                                  color: item.serviceType === 'TTS_RS' ? '#1e40af' : '#4338ca'
                                }}>
                                  {item.serviceType === 'TTS_RS' ? '설문' : '일반'}
                                </span>
                              </td>
                              <td style={{ padding: '12px 8px' }}>{item.phoneNumber}</td>
                              <td style={{ padding: '12px 8px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.message}
                              </td>
                              <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                {item.surveyAnswers && item.surveyAnswers.length > 0 ? (
                                  <span style={{
                                    padding: '4px 10px',
                                    backgroundColor: '#dcfce7',
                                    color: '#166534',
                                    borderRadius: '8px',
                                    fontSize: '0.85rem',
                                    fontWeight: '500'
                                  }}>
                                    {item.surveyAnswers.map(a => a.answer).join(', ')}
                                  </span>
                                ) : (
                                  <span style={{ color: '#9ca3af' }}>-</span>
                                )}
                              </td>
                              <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '12px',
                                  fontSize: '0.85rem',
                                  fontWeight: '500',
                                  backgroundColor: item.status === 'success' ? '#d1fae5' : item.status === 'failed' ? '#fee2e2' : '#fef3c7',
                                  color: item.status === 'success' ? '#065f46' : item.status === 'failed' ? '#991b1b' : '#92400e'
                                }}>
                                  {item.status === 'success' && '✓ 성공'}
                                  {item.status === 'failed' && '✗ 실패'}
                                  {item.status === 'pending' && '대기중'}
                                </span>
                              </td>
                              <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                                {item.cost.toLocaleString()}원
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ marginTop: '1rem', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                          <span>총 발신 건수: <strong>{fullSurveyHistory.length}건</strong></span>
                          <span>성공: <strong style={{ color: '#065f46' }}>{fullSurveyHistory.filter(h => h.status === 'success').length}건</strong></span>
                          <span>실패: <strong style={{ color: '#991b1b' }}>{fullSurveyHistory.filter(h => h.status === 'failed').length}건</strong></span>
                          <span>총 비용: <strong>{fullSurveyHistory.reduce((sum, h) => sum + h.cost, 0).toLocaleString()}원</strong></span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowFullHistoryModal(false)}
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TtsSurvey;
